import React, { useEffect, useState } from 'react';
import api from '../../utils/axiosInstance';
import CarrierHeader from '../../components/CarrierHeader';
import { loadStripe } from '@stripe/stripe-js';

import SpecialPackageCard from '../../components/SpecialPackageCard';
import ActiveSpecialPackageCard from '../../components/ActiveSpecialPackageCard';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Modal = ({ open, onClose, children }) => {
    if (!open) return null;
    return (
        <div className='fixed z-50 inset-0 bg-black/40 flex items-center justify-center'>
            <div className='bg-white rounded-lg shadow-xl p-6 relative w-full max-w-md mx-auto'>
                <button
                    className='absolute top-3 right-3 text-gray-400 hover:text-blue-700 text-2xl'
                    onClick={onClose}
                    aria-label='Close'
                >
                    &times;
                </button>
                {children}
            </div>
        </div>
    );
};

const PackageSelection = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [subscribedId, setSubscribedId] = useState('');
    const [subscriptionExpiry, setSubscriptionExpiry] = useState(null);
    const [emailSentPopup, setEmailSentPopup] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [subscribedPackage, setSubscribedPackage] = useState(null);

    // to track if they own the Special Package
    const [subscribedSpecialPackage, setSubscribedSpecialPackage] = useState(null);

    console.log(subscribedPackage, 'subscribedPackage');

    useEffect(() => {
        const fetchData = async () => {
            const storedUser = JSON.parse(localStorage.getItem('carrierUser'));
            const carrierId = storedUser?._id;
            if (!carrierId) return;

            try {
                // Fetch the Special Package actively right before doing standard packages
                try {
                    const specialRes = await api.get(`/special-package/my-purchases/${carrierId}`);
                    if (specialRes.data.success && specialRes.data.purchases.length > 0) {
                        setSubscribedSpecialPackage(specialRes.data.purchases[0]);
                    }
                } catch (err) {
                    console.log('No special package active');
                }

                const subRes = await api.get(`/subscribed/by-carrier/${carrierId}?t=${Date.now()}`, {
                    headers: { 'Cache-Control': 'no-cache' },
                });

                console.log('Subscription response:', subRes.data);

                // Check if subscription is active and has package data
                if (
                    subRes.data?.success &&
                    subRes.data?.active &&
                    subRes.data?.package &&
                    subRes.data?.status?.toLowerCase() === 'active'
                ) {
                    const packageData = subRes.data.package;

                    // ✅ Set subscribed package ID to filter it out from available packages
                    setSubscribedId(packageData._id);

                    setSubscriptionId(subRes.data.subscriptionId || null);

                    setSubscriptionExpiry(subRes.data.currentPeriodEnd ? new Date(subRes.data.currentPeriodEnd) : null);

                    // ✅ FIX: Properly merge package data with subscription data
                    setSubscribedPackage({
                        // Package details
                        _id: packageData._id,
                        name: packageData.name,
                        code: packageData.code,
                        price: packageData.price,
                        duration: packageData.duration,
                        accessDetails: packageData.accessDetails,
                        features: packageData.features,

                        // Subscription details
                        subscriptionId: subRes.data.subscriptionId,
                        carrierId,
                        autoPayment: subRes.data.autoPayment,
                        status: subRes.data.status,
                        currentPeriodEnd: subRes.data.currentPeriodEnd,
                    });

                    // ✅ Clear available packages since user has active subscription
                    // setPackages([]);
                } else {
                    // ✅ No active subscription - fetch available packages
                    const pkgRes = await api.get(`/packages/active?t=${Date.now()}`, {
                        headers: { 'Cache-Control': 'no-cache' },
                    });

                    setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
                    setSubscribedPackage(null);
                    setSubscriptionExpiry(null);
                    setSubscribedId('');
                }
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    // No subscription found - fetch available packages
                    const pkgRes = await api.get(`/packages/active?t=${Date.now()}`, {
                        headers: { 'Cache-Control': 'no-cache' },
                    });
                    setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
                    setSubscribedPackage(null);
                    setSubscriptionExpiry(null);
                    setSubscribedId('');
                } else {
                    console.error('Error fetching subscription/packages:', err);
                    setSubscribedPackage(null);
                    setPackages([]);
                    setSubscriptionExpiry(null);
                    setSubscribedId('');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []); // ✅ Removed the polling - only fetch once on mount

    const handleView = (pkg) => setSelectedPackage(pkg);

    const isSubscriptionActive = () => {
        if (!subscriptionExpiry) return false;
        return new Date() < subscriptionExpiry;
    };

    const handleStripeCheckout = async () => {
        if (!selectedPackage) return;

        const storedUser = JSON.parse(localStorage.getItem('carrierUser'));
        console.log(storedUser, 'userData');
        const priceId = selectedPackage.stripePriceId;

        console.log('✅ Using Price ID:', priceId);

        if (!priceId) {
            console.error('❌ No Stripe priceId found in package:', selectedPackage);
            alert('Stripe checkout failed: Package price ID not configured.');
            return;
        }

        try {
            const { data } = await api.post('/payment/create-stripe-subscription-session', {
                packageId: selectedPackage._id,
                packageName: selectedPackage.name,
                priceId: priceId,
                carrierId: storedUser?._id,
                customerEmail: storedUser?.email,
                success_url: `${window.location.origin}/carrier/payment-success?session_id={CHECKOUT_SESSION_ID}&packageId=${selectedPackage._id}`,
                cancel_url: `${window.location.origin}/carrier/packages`,
            });

            const sessionId = data?.id;
            if (!sessionId || typeof sessionId !== 'string') {
                console.error('❌ Invalid Stripe session ID:', data);
                alert('Stripe checkout failed: Invalid session ID.');
                return;
            }

            setEmailSentPopup(true);
            setTimeout(() => setEmailSentPopup(false), 3000);

            setTimeout(async () => {
                const stripe = await stripePromise;
                await stripe.redirectToCheckout({ sessionId });
            }, 500);
        } catch (err) {
            console.error('❌ Stripe checkout error:', err.response?.data || err.message);
            alert('Stripe checkout failed.');
        }
    };

    return (
        <div className='bg-gray-100 text-dark min-h-screen lg:pl-64'>
            <CarrierHeader />
            <div className='max-w-7xl mx-auto px-4 py-10'>
                {loading ? (
                    <div className='text-center py-20 text-xl text-gray-500'>Loading packages...</div>
                ) : subscribedPackage &&
                  ((subscriptionExpiry && isSubscriptionActive()) ||
                      subscribedPackage.status?.toLowerCase() === 'active') ? (
                    // ✅ Active Subcription Card
                    <div className='flex flex-wrap gap-8 items-start mt-6'>
                        <div className='mt-6 w-full max-w-md rounded-2xl bg-white shadow-lg p-4 space-y-4 mb-8'>
                            <h2 className='text-xl font-semibold text-gray-800 border-b pb-2'>
                                Your Active Subscription
                            </h2>

                            <div className='grid grid-cols-2 gap-y-3 text-gray-700'>
                                <span className='font-medium'>Name:</span>
                                <span>{subscribedPackage.name}</span>

                                <span className='font-medium'>Price:</span>
                                <span>${subscribedPackage.price}</span>

                                <span className='font-medium'>Duration:</span>
                                <span>{subscribedPackage.duration} Days</span>

                                <span className='font-medium'>Access:</span>
                                <span>{subscribedPackage.accessDetails} Logins</span>

                                <span className='font-medium'>Code:</span>
                                <span>{subscribedPackage.code}</span>

                                <span className='font-medium'>Valid Until:</span>
                                <span className='text-green-600 font-semibold'>
                                    {subscriptionExpiry
                                        ? new Date(subscriptionExpiry).toLocaleDateString('en-US', {
                                              month: '2-digit',
                                              day: '2-digit',
                                              year: 'numeric',
                                          })
                                        : 'N/A'}
                                </span>
                            </div>

                            <div
                                className={`mt-4 rounded-lg px-4 py-2 text-center font-medium ${
                                    subscribedPackage.autoPayment
                                        ? 'bg-green-100 text-green-700 border border-green-300'
                                        : 'bg-red-100 text-red-700 border border-red-300'
                                }`}
                            >
                                {subscribedPackage.autoPayment
                                    ? '✔ Auto-payment is active'
                                    : '✖ Auto-payment is disabled'}
                            </div>

                            {/* Cancel Autopay Button */}
                            {subscribedPackage.autoPayment && (
                                <div className='w-full rounded-md bg-slate-100 p-4 space-y-3'>
                                    <div className='bg-blue-50 border border-blue-200 rounded p-3 mb-2'>
                                        <p className='text-sm text-blue-800'>
                                            ℹ️ <strong>Note:</strong> Canceling autopay will stop future renewals, but
                                            you'll keep full access until{' '}
                                            <strong>
                                                {new Date(subscriptionExpiry).toLocaleDateString('en-US', {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                            </strong>
                                        </p>
                                    </div>

                                    <button
                                        onClick={async () => {
                                            const confirmed = window.confirm(
                                                `Are you sure you want to cancel autopay?\n\n` +
                                                    `You'll keep access until ${new Date(
                                                        subscriptionExpiry,
                                                    ).toLocaleDateString()}, ` +
                                                    `but won't be charged again unless you reactivate.`,
                                            );

                                            if (!confirmed) return;

                                            try {
                                                const res = await api.post('/payment/cancel-stripe-subscription', {
                                                    carrierId: subscribedPackage.carrierId,
                                                });

                                                alert(res.data.message);

                                                setSubscribedPackage((prev) => ({
                                                    ...prev,
                                                    autoPayment: false,
                                                }));
                                            } catch (err) {
                                                console.error('Cancel autopay error:', err.response?.data || err);
                                                alert('Failed to cancel autopay');
                                            }
                                        }}
                                        className='w-full bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition'
                                    >
                                        Cancel Autopay
                                    </button>
                                </div>
                            )}

                            {/* Reactivate Autopay Section */}
                            {!subscribedPackage.autoPayment && subscribedPackage.status === 'active' && (
                                <>
                                    <div className='mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
                                        <p className='text-sm text-yellow-800'>
                                            ⚠️ Your subscription will end on{' '}
                                            <strong>
                                                {subscriptionExpiry
                                                    ? new Date(subscriptionExpiry).toLocaleDateString('en-US', {
                                                          month: 'long',
                                                          day: 'numeric',
                                                          year: 'numeric',
                                                      })
                                                    : 'the end of the current period'}
                                            </strong>
                                            . Click "Reactivate Autopay" to continue your subscription.
                                        </p>
                                    </div>

                                    <div className='w-full text-sm rounded-md bg-green-100 flex p-2 items-center justify-center'>
                                        <button
                                            onClick={async () => {
                                                if (!subscribedPackage.carrierId) {
                                                    alert('Subscription data missing');
                                                    return;
                                                }
                                                try {
                                                    const res = await api.post(
                                                        '/payment/reactivate-stripe-subscription',
                                                        {
                                                            carrierId: subscribedPackage.carrierId,
                                                        },
                                                    );

                                                    alert(res.data.message);

                                                    setSubscribedPackage((prev) => ({
                                                        ...prev,
                                                        autoPayment: true,
                                                        status: 'active',
                                                    }));
                                                } catch (err) {
                                                    console.error(
                                                        'Reactivate autopay error:',
                                                        err.response?.data || err,
                                                    );

                                                    if (err.response?.data?.requireNewSubscription) {
                                                        alert(
                                                            'Your subscription has ended. Please subscribe to a new package.',
                                                        );
                                                    } else {
                                                        alert('Failed to reactivate autopay');
                                                    }
                                                }
                                            }}
                                            className='bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition'
                                        >
                                            Reactivate Autopay
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        {/* <SpecialPackageCard
                            onPurchaseSuccess={(purchase) => {
                                // Optional: add a toast notification here after purchase
                                console.log('Special package purchased:', purchase);
                            }}
                        /> */}

                        {/*Check if they own the special package before picking which card to render */}
                        {subscribedSpecialPackage ? (
                            <ActiveSpecialPackageCard
                                activePurchase={subscribedSpecialPackage}
                                setActivePurchase={setSubscribedSpecialPackage}
                            />
                        ) : (
                            <SpecialPackageCard
                                onPurchaseSuccess={(purchase) => {
                                    console.log('Special package purchased:', purchase);
                                    // Optional: force an instant refresh or manually set setSubscribedSpecialPackage
                                }}
                            />
                        )}
                    </div>
                ) : packages.length === 0 ? (
                    <div className='text-center py-20 text-xl text-gray-500'>No available packages found.</div>
                ) : (
                    // ✅ Available Packages (only shown when no active subscription)
                    <>
                        <div className='flex items-center justify-between mb-8'>
                            <h2 className='text-3xl font-bold text-gray-900'>Available Packages</h2>
                            <button
                                onClick={() => (window.location.href = '/carrier/payment-history')}
                                className='py-2 px-4 rounded-lg text-white font-semibold text-sm shadow transition bg-blue-600 hover:bg-blue-700'
                            >
                                View Payment History
                            </button>
                        </div>

                        {showPopup && (
                            <div className='fixed top-20 left-1/2 transform -translate-x-1/2 bg-blue-500 text-black px-6 py-3 rounded-lg shadow-lg z-50'>
                                {selectedPackage?.name} Subscribed successfully!
                            </div>
                        )}
                        {emailSentPopup && (
                            <div className='fixed top-32 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50'>
                                Payment details sent to your email!
                            </div>
                        )}

                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-baseline'>
                            {packages.map((pkg) => (
                                <div
                                    key={pkg._id}
                                    className='bg-white shadow-sm rounded-lg border p-6 transition hover:shadow-md border-gray-200'
                                >
                                    <div className='flex items-center justify-between mb-4'>
                                        <h3 className='text-xl font-semibold text-gray-800'>{pkg.name}</h3>
                                        <span className='text-green-600 font-bold text-lg'>${pkg.price}</span>
                                    </div>
                                    <div className='mb-4'>
                                        <p className='text-sm text-gray-700 font-medium'>Features:</p>
                                        <ul className='list-disc list-inside mt-1 text-gray-600 text-sm space-y-1'>
                                            {Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                                                pkg.features.map((feature, index) => <li key={index}>{feature}</li>)
                                            ) : (
                                                <li>N/A</li>
                                            )}
                                        </ul>
                                    </div>
                                    <button
                                        onClick={() => handleView(pkg)}
                                        className='w-full mt-4 py-2 px-4 rounded-lg text-white font-semibold text-sm shadow transition bg-blue-600 hover:bg-blue-700'
                                    >
                                        View Package
                                    </button>
                                </div>
                            ))}
                            {/* <SpecialPackageCard
                                onPurchaseSuccess={(purchase) => {
                                    console.log('Special package purchased:', purchase);
                                }}
                            /> */}

                            {/* Check if they own the special package before picking which card to render*/}
                            {subscribedSpecialPackage ? (
                                <ActiveSpecialPackageCard
                                    activePurchase={subscribedSpecialPackage}
                                    setActivePurchase={setSubscribedSpecialPackage}
                                />
                            ) : (
                                <SpecialPackageCard
                                    onPurchaseSuccess={(purchase) => {
                                        console.log('Special package purchased:', purchase);
                                    }}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Modal remains the same */}
            <Modal open={!!selectedPackage} onClose={() => setSelectedPackage(null)}>
                {selectedPackage && (
                    <div className='fixed inset-0 flex items-center justify-center px-4 py-6 z-50'>
                        <div className='relative w-full max-w-[1200px] bg-white rounded-3xl shadow-2xl overflow-hidden min-h-[600px] flex flex-col md:flex-row'>
                            <button
                                onClick={() => setSelectedPackage(null)}
                                className='absolute top-4 right-4 text-white text-4xl font-extrabold z-10 p-2 rounded-full hover:bg-white/20 transition'
                            >
                                &times;
                            </button>

                            <div className='w-full md:w-2/3 p-10 bg-white'>
                                <h2 className='text-5xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-black to-blue-600 leading-normal'>
                                    {selectedPackage.name}
                                </h2>

                                <h3 className='text-2xl font-semibold mb-6 text-gray-800'>Package Benefits</h3>
                                <ul className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    {Array.isArray(selectedPackage.features) && selectedPackage.features.length > 0 ? (
                                        selectedPackage.features.map((f, i) => (
                                            <li key={i} className='flex items-start text-gray-800 text-base'>
                                                <svg
                                                    className='w-6 h-6 text-blue-600 mr-3 mt-1'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    viewBox='0 0 24 24'
                                                >
                                                    <path
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth='2'
                                                        d='M5 13l4 4L19 7'
                                                    />
                                                </svg>
                                                {f}
                                            </li>
                                        ))
                                    ) : (
                                        <li className='text-gray-600'>N/A</li>
                                    )}
                                </ul>
                                <div className='mt-4 p-10 bg-gray-800 rounded-lg text-black'>
                                    <p className='text-md text-gray-300'>
                                        Please note, your package will be automatically renewed every{' '}
                                        {selectedPackage.duration} days.
                                    </p>
                                </div>
                            </div>

                            <div className='w-full md:w-1/3 bg-gradient-to-b from-black to-blue-700 text-white p-10 flex flex-col justify-between'>
                                <div>
                                    <div className='mb-8'>
                                        <p className='text-sm text-gray-300'>Price</p>
                                        <p className='text-5xl font-bold'>${selectedPackage.price}</p>
                                    </div>

                                    <div className='mb-6'>
                                        <p className='text-sm text-gray-300'>Duration</p>
                                        <p className='text-lg'>{selectedPackage.duration} Days</p>
                                    </div>

                                    <div className='mb-6'>
                                        <p className='text-sm text-gray-300'>Access Details</p>
                                        <p className='text-lg'>{selectedPackage.accessDetails} Logins</p>
                                    </div>

                                    <div className='mb-6'>
                                        <p className='text-sm text-gray-300'>Code</p>
                                        <p className='text-lg'>{selectedPackage.code}</p>
                                    </div>
                                </div>

                                <div className='mt-8'>
                                    <div className='flex justify-center mt-4'>
                                        <button
                                            onClick={handleStripeCheckout}
                                            className='bg-white text-black rounded-lg h-12 w-28 shadow hover:shadow-md transition flex items-center justify-center font-semibold text-lg'
                                        >
                                            Click to Pay
                                        </button>
                                    </div>
                                    <div style={{ textAlign: 'center' }} className='mt-2'>
                                        <span
                                            style={{
                                                display: 'block',
                                                fontSize: '0.90em',
                                                color: '#fff',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            Powered by{' '}
                                            <span style={{ fontWeight: 600, letterSpacing: '1px' }}>STRIPE</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PackageSelection;
